/**
 * React Query hooks for members and subscription tiers.
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { membersApi, tiersApi, type ListMembersParams } from '../api/members';
import type { CreateMemberRequest, UpdateMemberRequest, CreateTierRequest } from '../types';

/** Query keys */
export const memberKeys = {
  all: ['members'] as const,
  lists: () => [...memberKeys.all, 'list'] as const,
  list: (siteSlug: string, params?: ListMembersParams) =>
    [...memberKeys.lists(), siteSlug, params] as const,
  details: () => [...memberKeys.all, 'detail'] as const,
  detail: (siteSlug: string, memberId: string) =>
    [...memberKeys.details(), siteSlug, memberId] as const,
  stats: (siteSlug: string) => [...memberKeys.all, 'stats', siteSlug] as const,
};

export const tierKeys = {
  all: ['tiers'] as const,
  lists: () => [...tierKeys.all, 'list'] as const,
  list: (siteSlug: string) => [...tierKeys.lists(), siteSlug] as const,
  details: () => [...tierKeys.all, 'detail'] as const,
  detail: (siteSlug: string, tierId: string) =>
    [...tierKeys.details(), siteSlug, tierId] as const,
};

// ============================================================================
// Members Hooks
// ============================================================================

/** List members for a site */
export function useMembers(siteSlug: string, params?: ListMembersParams) {
  return useQuery({
    queryKey: memberKeys.list(siteSlug, params),
    queryFn: () => membersApi.list(siteSlug, params),
    enabled: !!siteSlug,
  });
}

/** Get a single member */
export function useMember(siteSlug: string, memberId: string) {
  return useQuery({
    queryKey: memberKeys.detail(siteSlug, memberId),
    queryFn: () => membersApi.get(siteSlug, memberId),
    enabled: !!siteSlug && !!memberId,
  });
}

/** Get member statistics */
export function useMemberStats(siteSlug: string) {
  return useQuery({
    queryKey: memberKeys.stats(siteSlug),
    queryFn: () => membersApi.stats(siteSlug),
    enabled: !!siteSlug,
  });
}

/** Create a new member */
export function useCreateMember(siteSlug: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreateMemberRequest) => membersApi.create(siteSlug, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: memberKeys.lists() });
      queryClient.invalidateQueries({ queryKey: memberKeys.stats(siteSlug) });
    },
  });
}

/** Update a member */
export function useUpdateMember(siteSlug: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ memberId, data }: { memberId: string; data: UpdateMemberRequest }) =>
      membersApi.update(siteSlug, memberId, data),
    onSuccess: (_, { memberId }) => {
      queryClient.invalidateQueries({ queryKey: memberKeys.lists() });
      queryClient.invalidateQueries({ queryKey: memberKeys.detail(siteSlug, memberId) });
      queryClient.invalidateQueries({ queryKey: memberKeys.stats(siteSlug) });
    },
  });
}

/** Delete a member */
export function useDeleteMember(siteSlug: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (memberId: string) => membersApi.delete(siteSlug, memberId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: memberKeys.lists() });
      queryClient.invalidateQueries({ queryKey: memberKeys.stats(siteSlug) });
    },
  });
}

// ============================================================================
// Subscription Tiers Hooks
// ============================================================================

/** List subscription tiers for a site */
export function useTiers(siteSlug: string) {
  return useQuery({
    queryKey: tierKeys.list(siteSlug),
    queryFn: () => tiersApi.list(siteSlug),
    enabled: !!siteSlug,
  });
}

/** Get a single tier */
export function useTier(siteSlug: string, tierId: string) {
  return useQuery({
    queryKey: tierKeys.detail(siteSlug, tierId),
    queryFn: () => tiersApi.get(siteSlug, tierId),
    enabled: !!siteSlug && !!tierId,
  });
}

/** Create a new tier */
export function useCreateTier(siteSlug: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreateTierRequest) => tiersApi.create(siteSlug, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: tierKeys.lists() });
    },
  });
}

/** Delete a tier */
export function useDeleteTier(siteSlug: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (tierId: string) => tiersApi.delete(siteSlug, tierId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: tierKeys.lists() });
    },
  });
}
